import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CustomReportSearchService } from '@http/clients/reporting-app/custom-report/custom-report-search.service';
import { CustomReportUserFavoriteService } from '@http/clients/reporting-app/custom-report/custom-report-user-favorite.service';
import { EntityTypeCustomFieldDto, ReportResultColumnDto } from '@http/clients/reporting-app/reporting-api-proxies';
import { ColDef } from 'ag-grid-enterprise';
import { IDropdownPanel, ModalService, SnackbarNotificationService } from 'ngx-lib';
import { configs } from '../components/pages/reports/report-single/configs/configs';
import { mapping } from '../components/pages/reports/report-single/filters/mapping';
import { mergeCustomFieldColumnsGridSettings } from '../components/pages/reports/report-single/report-single-tabs/grid-tab/report-custom-field-columns.utils';
import { DEFAULT_FILTER_VALUES, IFilterValues } from '../components/pages/reports/types/filter-values.interface';
import { IFilterItem, IReportConfiguration } from '../components/pages/reports/types/report-config.interface';
import { WidgetType } from '../components/pages/reports/types/report-widget-type.enum';
import { ModalConfirmComponent } from '../components/shared/modals/modal-confirm/modal-confirm.component';
import { ModalReportSaveComponent } from '../components/shared/modals/modal-report-save/modal-report-save.component';
import { ModalReportShareComponent } from '../components/shared/modals/modal-report-share/modal-report-share.component';
import { ModalScheduleComponent } from '../components/shared/modals/modal-schedule/modal-schedule.component';
import { AwaiterService } from './awaiter.service';
import { GridService } from './grid.service';
import { ReportTemplateService } from './http/clients/reporting-app/report-template.service';
import { ReportColumnVisibilityService } from './report-column-visibility.service';
import { ReportFiltersStateService } from './report-filters-state.service';
import { ReportsService } from './reports.service';

function cloneFilterValues(source: IFilterValues): IFilterValues {
    return structuredClone(source);
}

@Injectable()
export class ReportSingleService {
    private readonly router = inject(Router);
    private readonly reportTemplateService = inject(ReportTemplateService);
    private readonly reportsService = inject(ReportsService);
    private readonly reportFiltersStateService = inject(ReportFiltersStateService);
    private readonly gridService = inject(GridService);
    private readonly reportColumnVisibilityService = inject(ReportColumnVisibilityService);
    private readonly modalService = inject(ModalService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly awaiterService = inject(AwaiterService);
    private readonly customReportUserFavoriteService = inject(CustomReportUserFavoriteService);
    private readonly customReportService = inject(CustomReportSearchService);

    readonly configuration = signal<IReportConfiguration<unknown> | undefined>(undefined);
    readonly filterValues = signal<IFilterValues>(cloneFilterValues(DEFAULT_FILTER_VALUES));
    readonly additionalFiltersEditorEpoch = signal(0);

    readonly reportData = signal<any>(undefined);
    /**
     * True after the template-based report has been executed (`run()`) or when a custom report load starts the same pipeline.
     * Together with {@link customReportId}, controls showing report content (tabs/data) versus the empty state for template routes.
     */
    readonly hasRunTemplateReport = signal(false);
    readonly currentTab = signal('Overview');
    readonly isLoading = signal(false);
    /**
     * False until the first load pipeline finishes (custom/catalog fetch + run, or template metadata only).
     * After that, further runs use {@link isLoading} per tab only.
     */
    readonly initialBootstrapComplete = signal(false);
    readonly customReportId = signal<number | undefined>(undefined);
    readonly reportTemplateName = signal<string>('');
    readonly catalogReportId = signal<string | undefined>(undefined);
    readonly reportId = signal<number | string | undefined>(undefined);
    readonly reportSubtitle = signal<string>('');

    readonly canUpdateReport = signal(false);
    readonly canDeleteReport = signal(false);
    readonly canScheduleReport = signal(false);
    readonly canAddToCatalog = signal(false);
    readonly canUpdateCatalog = signal(false);

    readonly overviewData = signal<{ boxes?: any[]; metrics?: any; charts?: any } | undefined>(undefined);
    readonly gridData = signal<any[]>([]);
    readonly customFieldColumns = signal<EntityTypeCustomFieldDto[]>([]);

    readonly gridSettings = computed<ColDef[]>(() => {
        const config = this.configuration();
        const gridWidget = config?.gridTab?.widgets?.find((w: { type: number }) => w.type === WidgetType.Grid);
        const baseSettings = (gridWidget as { settings?: ColDef[] })?.settings ?? [];
        const customFieldColumns = this.customFieldColumns();

        if (!customFieldColumns.length) {
            return baseSettings;
        }

        return mergeCustomFieldColumnsGridSettings(baseSettings, customFieldColumns);
    });

    readonly allowGrouping = computed(() => {
        return this.gridService.getGroupingAvailableCols(this.gridSettings()).length > 0;
    });

    readonly downloadPanel: IDropdownPanel = {
        items: [
            {
                id: 'csv-file',
                name: 'CSV File',
                action: () => this.downloadCSV(this.reportData()?.name ?? 'report')
            },
            {
                id: 'xlsx-file-raw',
                name: 'Excel File (w/ groups)',
                action: () =>
                    this.gridService.downloadExcel({
                        sheetName: `${this.reportData()?.name ?? 'report'} - With Groups`,
                        fileName: `${this.reportData()?.name ?? 'export'} - With Groups.xlsx`
                    })
            },
            {
                id: 'xlsx-file-table-without-groups',
                name: 'Excel File (w/out groups)',
                action: () => {
                    const keys = this.getExcelColumnKeysForWithoutGroups();

                    this.gridService.downloadExcel({
                        sheetName: `${this.reportData()?.name ?? 'report'} - Without Groups`,
                        fileName: `${this.reportData()?.name ?? 'export'} - Without Groups.xlsx`,
                        skipRowGroups: true,
                        shouldRowBeSkipped: (params) => params.node.group === true,
                        columnKeys: keys,
                        exportAsExcelTable: { showFilterButton: true }
                    });
                }
            }
        ]
    };

    setConfiguration(template: string): void {
        const config = configs[template as keyof typeof configs];
        if (config) {
            this.configuration.set(config);
            const initial = cloneFilterValues(DEFAULT_FILTER_VALUES);
            this.filterValues.set(initial);
            this.reportFiltersStateService.setAllFilters(initial, config);
        } else {
            this.router.navigate(['/reports']);
        }
    }

    async initializeReport(): Promise<void> {
        const config = this.configuration();
        const customId = this.customReportId();
        const catalogToken = this.catalogReportId();

        if (!config?.base?.serviceType) return;

        this.initialBootstrapComplete.set(false);
        this.reportColumnVisibilityService.reset();

        const isSharedRoute = catalogToken != null && catalogToken !== '';
        if (isSharedRoute) {
            await this.initializeCatalogReport();
            this.initialBootstrapComplete.set(true);
            if (!this.reportData()?.hasFullAccessToReport) this.showLimitedSharedAccessWarning();
        } else if (customId) {
            await this.initializeCustomReport();
        } else {
            await this.initializeTemplateReport();
            this.initialBootstrapComplete.set(true);
        }

        this.updateAllowedActions();
    }

    updateFilterValues(partial: Partial<IFilterValues>): void {
        this.filterValues.update((current) => ({ ...current, ...partial }));
        this.bindFiltersToEditors();
        this.reportFiltersStateService.setAllFilters(this.filterValues(), this.configuration());
    }

    setFilterValues(values: IFilterValues): void {
        this.filterValues.set(values);
        this.bindFiltersToEditors();
        this.reportFiltersStateService.setAllFilters(this.filterValues(), this.configuration());
    }

    onFiltersMainChanged(): void {
        this.syncFilterValuesFromEditors();
    }

    onAdditionalFilterChanged(): void {
        this.syncFilterValuesFromEditors();
    }

    bumpAdditionalFiltersEditors(): void {
        this.additionalFiltersEditorEpoch.update((n) => n + 1);
    }

    async saveAsCustom(): Promise<void> {
        await this.openSaveModal(false);
    }
    async save(): Promise<void> {
        if (!this.checkUpdatePermission()) return;

        const config = this.configuration();
        const reportData = this.reportData();
        if (!config?.base?.serviceType || !reportData) return;

        const reportService = this.reportsService.getService(config.base.serviceType) as {
            saveCustomReport: (payload: Record<string, unknown>) => Promise<any>;
        };

        const params = this.getFilterParamsForApi();
        const updated = await reportService.saveCustomReport({
            id: reportData.id,
            name: reportData.name,
            reportTemplateId: reportData.reportTemplateId,
            filterParameters: params,
            isFavorite: reportData.isFavorite,
            isPublic: reportData.isPublic,
            organizations:
                reportData.organizations?.length > 0 && reportData.isPublic ? reportData.organizations : null,
            groupingColumns: this.gridService.getColsNames(this.gridService.getGroupingState()),
            hiddenColumns: this.reportColumnVisibilityService.getHiddenColumns(),
            orderColumns: this.gridService.getColsOrder(this.gridSettings()),
            customReportPreferences: this.gridService.getCustomReportPreferences(
                reportData.name,
                reportData.id,
                reportData.customReportPreferences
            )
        });

        this.reportData.set(updated);
        this.updateAllowedActions();
        this.snackbarNotificationService.success('The report has been successfully updated');
    }
    share(): void {
        const config = this.configuration();
        const reportData = this.reportData();
        if (!config?.base?.serviceType || !reportData) return;

        const reportService = this.reportsService.getService(config.base.serviceType);
        const params = this.getFilterParamsForApi();
        this.modalService.open(ModalReportShareComponent, {
            reportService,
            reportData,
            filterParameters: params,
            hiddenColumns: this.reportColumnVisibilityService.getHiddenColumns(),
            groupingColumns: this.gridService.getColsNames(this.gridService.getGroupingState()),
            orderColumns: this.gridService.getColsOrder(this.gridSettings()),
            customReportPreferences: this.gridService.getCustomReportPreferences()
        });
    }
    async schedule(): Promise<void> {
        const reportData = this.reportData();
        if (!reportData) return;

        const response = await this.modalService.open(ModalScheduleComponent, {
            schedulerId: reportData.schedulerId,
            customReportId: this.customReportId(),
            optionsDynamicDate: this.getOptionsDynamicDate(),
            lastRun: reportData.lastRun
        });
        if (!response) return;

        this.reportData.update((data) => {
            if (!data) return data;
            if (response.schedulerStatusId) {
                return {
                    ...data,
                    schedulerStatusId: response.schedulerStatusId,
                    schedulerStatusName: this.getSchedulerStatusName(response.schedulerStatusId)
                };
            }
            if (response.deletedScheduler) {
                return {
                    ...data,
                    schedulerId: undefined,
                    schedulerStatusId: undefined,
                    schedulerStatusName: undefined,
                    schedulerFrequencyId: undefined
                };
            }
            if (response.changedScheduler) {
                return {
                    ...data,
                    schedulerId: response.changedScheduler.id,
                    schedulerStatusId: response.changedScheduler.schedulerStatusId,
                    schedulerStatusName: this.getSchedulerStatusName(response.changedScheduler.schedulerStatusId),
                    schedulerFrequencyId: response.changedScheduler.schedulerFrequencyId
                };
            }
            return data;
        });
    }
    async deleteReport(): Promise<void> {
        if (!this.checkDeletePermission()) return;
        const reportData = this.reportData();
        if (!reportData?.id) return;

        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Delete',
            cancelCaption: 'Cancel',
            content:
                'This report will be deleted including all the data. This action cannot be undone. Are you sure you want to delete it?',
            title: 'Delete Report',
            isReject: true
        });
        if (!responseOk) return;

        await this.customReportService.deactivate(reportData.id);
        this.snackbarNotificationService.success('The report has been successfully deleted');
        this.router.navigate(['/reports']);
    }
    async toggleFavorite(id: number | string): Promise<void> {
        const reportData = this.reportData();
        if (!reportData) return;

        const intId = typeof id === 'string' ? Number.parseInt(id, 10) : id;
        const isCustom = !!this.customReportId();
        const favorite = isCustom
            ? await this.customReportUserFavoriteService.toggleFavorite(intId)
            : await this.reportTemplateService.toggleFavorite(intId);

        this.reportData.update((data) => (data ? { ...data, isFavorite: favorite } : data));
        this.snackbarNotificationService.success(`Report ${favorite ? 'added' : 'removed'} from favorites`);
    }
    async edit(): Promise<void> {
        await this.openSaveModal(true);
    }

    async run(): Promise<void> {
        const config = this.configuration();
        if (!config?.base?.serviceType) return;

        const isFirstTemplateExecution = !this.hasRunTemplateReport();
        if (isFirstTemplateExecution) {
            this.initialBootstrapComplete.set(false);
        }
        this.hasRunTemplateReport.set(true);

        try {
            this.isLoading.set(true);
            this.customFieldColumns.set([]);
            const reportService = this.reportsService.getService(config.base.serviceType) as Record<
                string,
                (params: unknown) => Promise<unknown>
            >;
            const params = this.getFilterParamsForApi();

            const overviewWidgets = config.overviewTab?.widgets ?? [];
            const gridWidget = config.gridTab?.widgets?.find((w: { type: number }) => w.type === WidgetType.Grid);

            const boxes: unknown[] = [];
            let metrics: unknown;
            const charts: unknown[] = [];

            for (const widget of overviewWidgets) {
                const methodName = (widget as { methodName?: string }).methodName;
                if (!methodName || typeof reportService[methodName] !== 'function') continue;
                const result = await reportService[methodName](params);
                const wType = (widget as { type: number }).type;
                if (wType === WidgetType.Box) boxes.push(...((result as unknown[]) ?? []));
                else if (wType === WidgetType.Metrics) metrics = result;
                else if (wType === WidgetType.Chart) charts.push(result as never);
            }

            this.overviewData.set({ boxes, metrics, charts: charts.length ? charts : undefined });

            if (gridWidget) {
                await this.loadCustomFieldColumns(reportService, params);
                await this.loadResultAvailableColumns(reportService, params);
                const methodName = (gridWidget as { methodName?: string }).methodName;
                if (methodName && typeof reportService[methodName] === 'function') {
                    const gridResult = await reportService[methodName](params);
                    this.gridData.set((gridResult as unknown[]) ?? []);
                }
            } else {
                this.gridData.set([]);
            }
        } catch (error: any) {
            this.snackbarNotificationService.error(
                `An  error occurred while running the report: ${error?.message ?? error}`
            );
        } finally {
            this.isLoading.set(false);
            queueMicrotask(() => {
                this.applySavedGridSettings();
                queueMicrotask(() => this.initialBootstrapComplete.set(true));
            });
        }
    }

    onGroupingChanged(groupingColumns: string[]): void {
        this.reportData.update((data) => (data ? { ...data, groupingColumns } : data));
    }

    onHiddenColumnsChanged(): void {
        const hiddenColumns = this.reportColumnVisibilityService.getHiddenColumns();
        this.reportData.update((data) => (data ? { ...data, hiddenColumns } : data));
    }

    applySavedGridSettings(): void {
        const data = this.reportData();
        const api = this.gridService.getGridApi();
        if (!api || !data) return;

        const groupingColumns = (data.groupingColumns ?? []) as string[];

        this.ensureColumnVisibilityInitialized();
        this.reportColumnVisibilityService.applyToGrid();

        if (groupingColumns.length) {
            const gridCols = api.getColumns() ?? [];
            const resolvedGrouping = groupingColumns
                .map((key) => gridCols.find((c) => c.getColId() === key || c.getColDef().field === key)?.getColId())
                .filter((id): id is string => !!id);
            if (resolvedGrouping.length) {
                this.gridService.setGroupingState(resolvedGrouping);
                const groupedColDefs = resolvedGrouping
                    .map((colId) =>
                        this.gridSettings().find((c: ColDef) => (c.colId ?? c.field) === colId || c.field === colId)
                    )
                    .filter((c: ColDef | undefined): c is ColDef => !!c);
                if (groupedColDefs.length) {
                    this.gridService.setGroupingStateValue(groupedColDefs);
                }
            }
        }

        if (data.orderColumns?.length) {
            this.gridService.setColsOrder(data.orderColumns);
        }
        if (data.customReportPreferences?.length) {
            this.gridService.setCustomReportPreferences(data.customReportPreferences);
        }
    }

    clearAdditionalFilters(): void {
        const config = this.configuration();
        if (!config) return;
        const additionalFilters = config.filters.additional;
        if (!additionalFilters?.length) return;

        const values = this.filterValues() as Record<string, unknown>;
        for (const filter of additionalFilters) {
            if (filter.mapping) {
                Object.keys(filter.mapping).forEach((sourceKey: string) => {
                    const mappedPath = filter.mapping![sourceKey] ?? mapping[sourceKey];
                    if (!mappedPath) return;
                    const keys = mappedPath.split('.');
                    let target: Record<string, unknown> = values;
                    keys.forEach((key: string, idx: number) => {
                        if (idx === keys.length - 1) {
                            // Keep keys present in payload after clear (avoid serializer dropping `undefined`).
                            target[key] = null;
                        } else {
                            target[key] = typeof target[key] === 'object' && target[key] !== null ? target[key] : {};
                            target = target[key] as Record<string, unknown>;
                        }
                    });
                });
            } else if (filter.fieldName) {
                values[filter.fieldName] = null;
            }
        }
        this.filterValues.set({ ...values });
        this.bindFiltersToEditors();
        this.reportFiltersStateService.setAllFilters(this.filterValues(), this.configuration());
        this.bumpAdditionalFiltersEditors();
        this.reportsService.setSelectedProperty(true);
    }

    bindFiltersToEditors(): void {
        const config = this.configuration();
        if (!config) return;

        const values = this.filterValues();
        config.filters.main?.forEach((filter) => {
            filter.bindContext = values;
        });
        config.filters.additional?.forEach((filter) => {
            filter.bindContext = values;
        });
    }

    private showLimitedSharedAccessWarning(): void {
        this.snackbarNotificationService.warn("Some shared filters weren't applied due to limited access permissions.");
    }

    private async initializeTemplateReport(): Promise<void> {
        const template = this.reportTemplateName();
        try {
            const data = await this.reportTemplateService.getByRoute(`reports/${template}`);
            this.reportData.set(data);
            this.reportSubtitle.set(`Base Template - ${data?.reportTemplateGroupName ?? ''}`);
            this.reportId.set(data?.id);
        } catch {
            this.reportData.set({
                name: this.formatTemplateName(template),
                reportTemplateGroupName: ''
            });
            this.reportSubtitle.set('Base Template');
            this.reportId.set(undefined);
        }
    }

    private formatTemplateName(template: string): string {
        return template
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    private async initializeCustomReport(): Promise<void> {
        const config = this.configuration();
        const customId = this.customReportId();
        if (!config?.base?.serviceType || customId == null) return;

        const reportService = this.reportsService.getService(config.base.serviceType) as {
            getCustomReport: (id: number) => Promise<any>;
        };

        const data = await this.awaiterService.awaitAction('Getting Report', async () => {
            const loaded = await reportService.getCustomReport(customId);
            this.reportData.set(loaded);
            this.reportSubtitle.set(`${loaded?.reportTemplateName ?? ''} - ${loaded?.reportTemplateGroupName ?? ''}`);
            this.reportId.set(customId);

            if (loaded?.filterParameters) {
                this.initializeFilterValuesFromParams(loaded.filterParameters);
            }
            this.hasRunTemplateReport.set(true);
            return loaded;
        });

        if (data === undefined) {
            this.reportData.set(undefined);
            this.reportSubtitle.set('');
            this.reportId.set(undefined);
            this.hasRunTemplateReport.set(false);
            this.initialBootstrapComplete.set(true);
            return;
        }

        await this.run();
    }

    private downloadCSV(filename: string): void {
        const api = this.gridService.getGridApi();
        if (!api) return;

        const columnKeys = this.getExcelColumnKeysForWithoutGroups();
        api.exportDataAsCsv({
            fileName: filename,
            ...(columnKeys?.length ? { columnKeys } : {})
        });
    }

    private initializeFilterValuesFromParams(filterParams: Record<string, unknown>): void {
        const config = this.configuration();
        if (!config) return;

        const newValues = cloneFilterValues(DEFAULT_FILTER_VALUES);

        (['main', 'additional'] as const).forEach((category) => {
            const filters = config.filters[category] as IFilterItem[] | undefined;
            filters?.forEach((filter) => {
                if (filter.mapping) {
                    Object.keys(filter.mapping).forEach((sourceKey: string) => {
                        const value = filterParams[sourceKey];
                        const mappedPath = filter.mapping![sourceKey] ?? mapping[sourceKey];
                        if (!mappedPath) return;
                        const keys = mappedPath.split('.');
                        let target: Record<string, unknown> = newValues as Record<string, unknown>;
                        keys.forEach((key: string, idx: number) => {
                            if (idx === keys.length - 1) {
                                target[key] = value;
                            } else {
                                target[key] =
                                    typeof target[key] === 'object' && target[key] !== null ? target[key] : {};
                                target = target[key] as Record<string, unknown>;
                            }
                        });
                    });
                } else if (filter.fieldName) {
                    (newValues as Record<string, unknown>)[filter.fieldName] = filterParams[filter.fieldName];
                }
            });
        });

        this.filterValues.set(newValues);
        this.bindFiltersToEditors();
        this.reportFiltersStateService.setAllFilters(this.filterValues(), this.configuration());
        this.bumpAdditionalFiltersEditors();
        this.reportsService.setSelectedProperty(true);
    }

    private async initializeCatalogReport(): Promise<void> {
        const config = this.configuration();
        const catalogToken = this.catalogReportId();
        if (!config?.base?.serviceType || !catalogToken) return;

        const reportService = this.reportsService.getService(config.base.serviceType) as {
            getSharedReport: (token: string) => Promise<any>;
        };

        const data = await this.awaiterService.awaitAction('Getting Report', async () => {
            const loaded = await reportService.getSharedReport(catalogToken);
            this.reportData.set(loaded);
            this.reportSubtitle.set(`${loaded?.reportTemplateName ?? ''} - ${loaded?.reportTemplateGroupName ?? ''}`);
            this.reportId.set(catalogToken);

            if (loaded?.filterParameters) {
                this.initializeFilterValuesFromParams(loaded.filterParameters);
            }

            return loaded;
        });

        if (data === undefined) {
            this.reportData.set(undefined);
            this.reportSubtitle.set('');
            this.reportId.set(undefined);
            this.hasRunTemplateReport.set(false);
            return;
        }

        await this.run();
    }

    private updateAllowedActions(): void {
        this.canUpdateReport.set(this.checkUpdatePermission());
        this.canDeleteReport.set(this.checkDeletePermission());
        this.canScheduleReport.set(this.checkScheduleReportPermission());
        this.canAddToCatalog.set(this.checkAddToCatalogPermission());
        this.canUpdateCatalog.set(this.checkUpdateCatalogPermission());
    }

    private checkUpdatePermission(): boolean {
        const customId = this.customReportId();
        const data = this.reportData();
        return !!customId && (!data?.isPublic || (data?.isPublic && data?.canEditCatalogReport));
    }

    private checkDeletePermission(): boolean {
        const data = this.reportData();
        if (data?.isPublic) {
            return !!data.canDeleteCatalogReport;
        }
        return !!data?.reportTemplateId;
    }

    private checkScheduleReportPermission(): boolean {
        const data = this.reportData();
        return !data?.isPublic && this.checkUpdatePermission();
    }

    private checkAddToCatalogPermission(): boolean {
        const data = this.reportData();
        return !!data?.canAddToCatalog;
    }

    private checkUpdateCatalogPermission(): boolean {
        const data = this.reportData();
        const customId = this.customReportId();
        return !!customId && !!data?.isPublic && !!data?.canEditCatalogReport;
    }

    private async openSaveModal(editMode: boolean): Promise<void> {
        const config = this.configuration();
        const reportData = this.reportData();
        if (!config?.base?.serviceType || !reportData) return;

        const reportService = this.reportsService.getService(config.base.serviceType);
        const params = this.getFilterParamsForApi();
        const response = await this.modalService.open(ModalReportSaveComponent, {
            id: editMode ? this.customReportId() : undefined,
            canAddToCatalog: this.checkAddToCatalogPermission(),
            canUpdateCatalog: this.checkUpdateCatalogPermission(),
            reportService,
            reportData,
            filterParameters: params,
            editMode,
            hiddenColumns: this.reportColumnVisibilityService.getHiddenColumns(),
            groupingColumns: this.gridService.getColsNames(this.gridService.getGroupingState()),
            orderColumns: this.gridService.getColsOrder(this.gridSettings()),
            customReportPreferences: this.gridService.getCustomReportPreferences()
        });

        if (response && response.id === this.customReportId()) {
            this.reportData.set(response);
            this.updateAllowedActions();
        }
    }

    private getSchedulerStatusName(statusId: number): string {
        if (statusId === 1) return 'Active';
        if (statusId === 2) return 'Paused';
        return '';
    }

    private async loadResultAvailableColumns(
        reportService: Record<string, (params: unknown) => Promise<unknown>>,
        params: Record<string, unknown>
    ): Promise<void> {
        if (typeof reportService['getResultAvailableColumns'] !== 'function') {
            this.initColumnVisibilityFromGridSettings();
            return;
        }

        const organizationIds = this.extractOrganizationIds(params);
        const hiddenColumns = (this.reportData()?.hiddenColumns ?? []) as string[];

        try {
            const cols = (await reportService['getResultAvailableColumns'](
                organizationIds.length ? organizationIds : undefined
            )) as ReportResultColumnDto[];

            if (cols?.length) {
                this.reportColumnVisibilityService.initFromApiColumns(cols, hiddenColumns);
                return;
            }
        } catch {
            // Fall back to grid config columns when the API call fails.
        }

        this.initColumnVisibilityFromGridSettings();
    }

    ensureColumnVisibilityInitialized(): void {
        if (this.reportColumnVisibilityService.availableColumns().length) return;
        this.initColumnVisibilityFromGridSettings();
    }

    private initColumnVisibilityFromGridSettings(): void {
        const cols = this.mapGridSettingsToAvailableColumns(this.gridSettings());
        if (!cols.length) {
            this.reportColumnVisibilityService.reset();
            return;
        }

        const hiddenColumns = (this.reportData()?.hiddenColumns ?? []) as string[];
        this.reportColumnVisibilityService.initFromApiColumns(cols, hiddenColumns);
    }

    private mapGridSettingsToAvailableColumns(colDefs: ColDef[]): ReportResultColumnDto[] {
        return colDefs
            .map((col) => {
                const propertyName = col.colId ?? col.field;
                if (!propertyName) return null;

                return {
                    displayName: col.headerName ?? propertyName,
                    propertyName,
                    isFrozen: !!col.lockVisible,
                    isCustomFieldColumn: propertyName.startsWith('customField_')
                };
            })
            .filter((col): col is ReportResultColumnDto => col !== null);
    }

    private extractOrganizationIds(params: Record<string, unknown>): number[] {
        return Array.isArray(params['organizationIds'])
            ? params['organizationIds'].filter((id): id is number => typeof id === 'number')
            : [];
    }

    private async loadCustomFieldColumns(
        reportService: Record<string, (params: unknown) => Promise<unknown>>,
        params: Record<string, unknown>
    ): Promise<void> {
        if (typeof reportService['getCustomFieldColumns'] !== 'function') {
            this.customFieldColumns.set([]);
            return;
        }

        const organizationIds = Array.isArray(params['organizationIds'])
            ? params['organizationIds'].filter((id): id is number => typeof id === 'number')
            : [];

        if (organizationIds.length !== 1) {
            this.customFieldColumns.set([]);
            return;
        }

        const customFieldColumns = (await reportService['getCustomFieldColumns'](
            organizationIds
        )) as EntityTypeCustomFieldDto[];
        this.customFieldColumns.set(customFieldColumns ?? []);
    }

    private getOptionsDynamicDate() {
        const config = this.configuration();
        if (!config) return [];

        return config.filters.additional
            .filter((filter) => filter.componentId === 'date-range' && filter.fieldName)
            .map((filter) => {
                const field = filter.fieldName;
                const optionName = field === 'expectedResponseDate' ? 'expectedBookingDate' : field;
                return {
                    value: field.charAt(0).toUpperCase() + field.slice(1),
                    name: optionName.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())
                };
            });
    }

    // Syncs filters and builds the API payload.
    private getFilterParamsForApi(): Record<string, unknown> {
        this.syncFilterValuesFromEditors();
        this.reportFiltersStateService.setAllFilters(this.filterValues(), this.configuration());
        return this.reportFiltersStateService.toApiPayload(this.configuration());
    }

    // Pulls filter values from bound editor context.
    private syncFilterValuesFromEditors(): void {
        const config = this.configuration();
        if (!config) return;

        const allFilters = [...(config.filters.main ?? []), ...(config.filters.additional ?? [])];
        const context = allFilters.find((filter) => filter.bindContext)?.bindContext as IFilterValues | undefined;
        if (!context) return;

        this.filterValues.set(cloneFilterValues(context));
    }

    private getExportableColumnKey(col: {
        getColId?: () => string;
        getColDef?: () => { field?: string };
    }): string | null {
        const colId = col.getColId?.();
        if (!colId) return null;

        const colDef = col.getColDef?.();
        if (colDef?.field || colId.startsWith('customField_')) {
            return colId;
        }

        return null;
    }

    // When `rowGroup` is enabled, Ag-Grid typically hides the grouped column in the table.
    // For the "w/out groups" Excel export we want to skip the group *rows* but still include the grouped column.
    private getExcelColumnKeysForWithoutGroups(): string[] | undefined {
        const api = this.gridService.getGridApi();
        const visibleCols = this.gridService.getVisibleCols();

        const visibleKeys = visibleCols
            .map((col) => this.getExportableColumnKey(col))
            .filter((key): key is string => !!key);

        const groupByCols = api?.getRowGroupColumns?.() ?? [];
        const groupByKeys = groupByCols
            .map((col) => this.getExportableColumnKey(col))
            .filter((key): key is string => !!key);

        const keySet = new Set<string>([...visibleKeys, ...groupByKeys]);
        if (!keySet.size) return undefined;

        // Keep the same column order the user currently has in the grid.
        // This preserves the original position of group-by columns (even if Ag-Grid hides them when grouped).
        const orderedColIds = api?.getState?.().columnOrder?.orderedColIds ?? [];

        if (!orderedColIds.length) {
            return [...keySet];
        }

        const orderedKeys = orderedColIds.filter((colId: string) => keySet.has(colId));
        const missingKeys = [...keySet].filter((colId) => !orderedKeys.includes(colId));
        return orderedKeys.length || missingKeys.length ? [...orderedKeys, ...missingKeys] : undefined;
    }
}
